import './ListEntity.css';
import { useMaybeSoupView } from '@app/features/next-soup/soup-view/soup-view-context';
import { useFeatureFlag } from '@app/lib/analytics/posthog';
import {
  SwipableRow,
  SwipableRowContext,
} from '@components/app/mobile/SwipableRow';
import { useSplitPanel } from '@components/app/split-layout/layoutUtils';
import { isMobile } from '@core/mobile/isMobile';
import { isTouchDevice } from '@core/mobile/isTouchDevice';
import { stackNotifications } from '@notifications';
import {
  BULK_DOCUMENT_WAKEUP_FEATURE_FLAG,
  enqueueDocumentWakeup,
  isWakeableDocument,
} from '@queries/preview';
import {
  getStreamState,
  subscribeToStreamState,
} from '@service-connection/stream-events';
import { mergeRefs } from '@solid-primitives/refs';
import { cn } from '@ui';
import {
  createEffect,
  createMemo,
  createSignal,
  type JSX,
  Match,
  Show,
  Switch,
  useContext,
} from 'solid-js';
import { Entity } from '../entity';
import type { EntityRowConfig } from '../extractors-notification';
import {
  isHitSnippetComplete,
  isHitSnippetEntity,
} from '../extractors-search/snippet-entity';
import { isChannelEntity, isEmailEntity } from '../types/entity';
import { isWithNotification } from '../types/notification';
import { isSearchEntity } from '../types/search';
import { createEntityDraggable } from '../utils/draggable';
import { unreadFilterFn } from '../utils/filter';
import {
  filterNotDoneNotifications,
  filterValidNotifications,
} from '../utils/notification';
import { useIsShared } from '../utils/shared';
import { NarrowCondensedLayout } from './list-entity/narrow-condensed-layout';
import { NarrowInboxLayout } from './list-entity/narrow-inbox-layout';
import { NarrowLayout } from './list-entity/narrow-layout';
import { NarrowSingleLineLayout } from './list-entity/narrow-single-line-layout';
import { SOUP_ROW_CLASS } from './list-entity/row-geometry';
import {
  type BaseListEntityProps,
  hasSearchContentHits,
  type LayoutProps,
  useCharacterCount,
  useListLayout,
} from './list-entity/shared';
import { WideLayout } from './list-entity/wide-layout';

export {
  ListLayoutProvider,
  type NarrowLayoutVariant,
} from './list-entity/shared';

interface ListEntityProps extends BaseListEntityProps {
  showUnrollNotifications?: boolean;
}

export function MaybeEntityRow(props: {
  entityId: string;
  children: JSX.Element;
  config?: EntityRowConfig;
}) {
  const ctx = useContext(SwipableRowContext);
  return (
    <Show when={isTouchDevice() && ctx} fallback={props.children}>
      <SwipableRow
        id={props.entityId}
        swipeLeftColor={props.config?.swipeLeftColor}
        swipeLeftRevealedComponent={props.config?.swipeLeftRevealedComponent}
        swipeRightColor={props.config?.swipeRightColor}
        swipeRightRevealedComponent={props.config?.swipeRightRevealedComponent}
      >
        {props.children}
      </SwipableRow>
    </Show>
  );
}

export function ListEntity(props: ListEntityProps) {
  // Legacy Soup callers do not pass row behavior explicitly yet.
  const soupView = useMaybeSoupView();

  const unread = () => unreadFilterFn(props.entity);
  const isShared = useIsShared(props.entity);
  const bulkWakeupEnabled = useFeatureFlag(BULK_DOCUMENT_WAKEUP_FEATURE_FLAG);

  createEffect(() => {
    if (!bulkWakeupEnabled().enabled) return;
    if (!isWakeableDocument(props.entity)) return;

    enqueueDocumentWakeup(props.entity);
  });

  subscribeToStreamState(props.entity.id, props.entity.type);
  const streamState = getStreamState(props.entity.id);

  const hasNotifications = () => {
    if (!props.showUnrollNotifications) return false;
    if (!isWithNotification(props.entity)) return false;
    return (
      filterNotDoneNotifications(
        filterValidNotifications(props.entity.notifications?.())
      ).length > 0
    );
  };

  const [snippetContainerRef, setSnippetContainerRef] = createSignal<
    HTMLElement | undefined
  >();
  const chars = useCharacterCount(() =>
    props.deferInteractions && !hasSearchContentHits(props.entity)
      ? undefined
      : snippetContainerRef()
  );

  // For singleton hits on a SnippetEntity, expanding "show more" only adds
  // value when windowSearchMatch trimmed text — otherwise the inline
  // snippet already shows everything.
  const isContentHitsRedundant = () => {
    if (!isHitSnippetEntity(props.entity)) return false;
    if (!isSearchEntity(props.entity)) return false;
    const hits = props.entity.search.contentHitData;
    if (!hits || hits.length !== 1) return false;
    return isHitSnippetComplete(hits[0].content, chars());
  };

  // Render the highlighted hit snippet whenever the entity has hits — even
  // if the expandable panel is suppressed as redundant, the inline snippet
  // should still highlight the match.
  const showHitSnippet = () =>
    !props.hideContentHits && hasSearchContentHits(props.entity);

  const showContentHits = () => showHitSnippet() && !isContentHitsRedundant();

  const layoutProps = (): LayoutProps => ({
    entity: props.entity,
    checked: props.checked,
    hideCheckbox: props.hideCheckbox,
    onChecked: props.onChecked,
    unread: unread(),
    isShared: isShared(),
    hasNotifications: hasNotifications(),
    showHitSnippet: showHitSnippet(),
    streamState: streamState(),
    setSnippetContainerRef,
    chars: chars(),
    onProjectClick: props.onProjectClick,
    onFilterByTag: props.onFilterByTag ?? soupView?.filterByTag,
    showCalendarAttendance:
      props.showCalendarAttendance ??
      (soupView?.activeTab() ?? 'all') === 'all',
  });

  const draggable = createEntityDraggable({
    entity: props.entity,
    splitId: useSplitPanel()?.handle?.id,
    deferUntilInteraction: () => props.deferInteractions === true,
  });

  const listLayout = useListLayout();
  const isWide = listLayout?.isWide ?? (() => true);
  const isCondensedVariant = () => listLayout?.narrowLayout() === 'condensed';
  const usesCondensedNarrowLayout = () => !isWide() && isCondensedVariant();
  const isSingleLineVariant = () =>
    listLayout?.narrowLayout() === 'single-line';

  const mobileStacks = createMemo(() => {
    if (!isTouchDevice()) return [];
    if (!props.showUnrollNotifications) return [];
    const notifs = props.entity.notifications?.();
    if (!notifs?.length) return [];
    const validNotifs = filterNotDoneNotifications(
      filterValidNotifications(notifs)
    );
    if (!validNotifs.length) return [];
    return stackNotifications(validNotifs);
  });

  return (
    <Entity.Root
      entity={props.entity}
      onClick={(e) => {
        if (e.metaKey && props.onChecked) {
          props.onChecked(!props.checked, e.shiftKey);
          return;
        }
        props.onClick?.(e);
      }}
      ref={mergeRefs(props.ref, draggable)}
      class={cn(
        // The row family class carries the --soup-row-* geometry (ListEntity.css)
        // that the layouts below and the soup group headers both read, so it has
        // to track which layout the Switch actually renders.
        isWide() ? SOUP_ROW_CLASS.wide : SOUP_ROW_CLASS.narrow,
        'soup-list-entity rounded-xl @container/entity w-[calc(100%-0.5rem)] mr-1 relative group/narrow flex flex-col py-0.5',
        {
          'min-h-10 mx-(--soup-row-gutter)':
            !isMobile() && !usesCondensedNarrowLayout(),
          'min-h-9 mx-(--soup-row-gutter)':
            !isMobile() && usesCondensedNarrowLayout(),
          'bg-list-selected': props.checked,
          'bg-list-selected-highlighted': props.checked && props.highlighted,
          'bg-list-highlighted':
            props.highlighted && !props.checked && !isTouchDevice(),
          'hover:bg-list-hover':
            !props.highlighted && !props.checked && !isTouchDevice(),
        }
      )}
      onMouseMove={props.onMouseMove}
    >
      <Switch>
        <Match when={isWide()}>
          <MaybeEntityRow
            entityId={props.entity.id}
            config={props.entityRowConfig}
          >
            <WideLayout {...layoutProps()} />
          </MaybeEntityRow>
        </Match>
        {/* Mixed-type lists (Search) render every entity as one line, ahead
            of the taller per-type mobile layouts below. */}
        <Match when={isSingleLineVariant()}>
          <MaybeEntityRow
            entityId={props.entity.id}
            config={props.entityRowConfig}
          >
            <NarrowSingleLineLayout {...layoutProps()} />
          </MaybeEntityRow>
        </Match>
        <Match when={isTouchDevice() && mobileStacks().length > 0}>
          <Entity.Notification.MobileStackRows
            stacks={mobileStacks()}
            entity={props.entity}
            entityRowConfig={props.entityRowConfig}
          />
        </Match>
        <Match
          when={
            isTouchDevice() &&
            ((isChannelEntity(props.entity) && !isCondensedVariant()) ||
              isEmailEntity(props.entity) ||
              props.showUnrollNotifications)
          }
        >
          <MaybeEntityRow
            entityId={props.entity.id}
            config={props.entityRowConfig}
          >
            <NarrowInboxLayout {...layoutProps()} />
          </MaybeEntityRow>
        </Match>
        <Match when={!isMobile() && usesCondensedNarrowLayout()}>
          <MaybeEntityRow
            entityId={props.entity.id}
            config={props.entityRowConfig}
          >
            <NarrowCondensedLayout {...layoutProps()} />
          </MaybeEntityRow>
        </Match>
        <Match when={true}>
          <MaybeEntityRow
            entityId={props.entity.id}
            config={props.entityRowConfig}
          >
            <NarrowLayout {...layoutProps()} />
          </MaybeEntityRow>
        </Match>
      </Switch>

      <Show when={hasNotifications() && !isTouchDevice()}>
        <div class="px-2 pb-1.5 -mt-1 min-w-0 overflow-hidden">
          <div class={cn('min-w-0 flex-1 ml-2 @lg/entity:ml-6')}>
            <Show when={isWithNotification(props.entity) && !showContentHits()}>
              <Entity.Notification.Stacks
                entity={props.entity}
                visibleCount={3}
              />
            </Show>
          </div>
        </div>
      </Show>

      <Show when={showContentHits()}>
        <div class="flex gap-2 size-full items-center text-sm px-2 pb-1 -mt-2 min-w-0">
          <div
            class={cn('min-w-0 flex-1 overflow-hidden ml-4 @lg/entity:ml-6')}
          >
            <Entity.Search.ContentHits
              entity={props.entity}
              onClick={props.onContentHitClick}
              visibleCount={0}
            />
          </div>
        </div>
      </Show>
    </Entity.Root>
  );
}
